import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { BaseFormComponent } from '@app/forms/base-form/base-form';
import { ApiService } from '@app/services/api/api.service';
import { map, Observable, of, shareReplay, take } from 'rxjs';
import {
  AutocompleteItem,
  AutocompleteOption,
} from '@app/forms/formly/types/input-field-with-autocomplete/input-field-with-autocomplete.component';
import { Option } from '@app/forms/formly/formly-utils';
import { AbstractControl } from '@angular/forms';
import {
  Range,
  RangeData,
} from '@app/forms/formly/types/range-picker-dropdown/range-picker-dropdown.component';
import {
  formatStringToDate,
  isValidDateString,
} from '@app/forms/formly/types/range-picker-dropdown/range-picker-dropdown.helpers';
import { toISO8601 } from '@app/utils/date';
import { ApiProtectedService } from '@app/services/api/api.protected.service';
import { Router } from '@angular/router';
import { PopupsService } from '@voyagu/portal-design-system';
import { NewLead, PortalServiceClassType } from '@app/services/api/api.types';
import { ErrorService } from '@app/services/error.service';
import { handleError } from '@app/operators/handle-error';

export interface Airport {
  id: string;
  cityName: string;
  locationCode: string;
  locationName: string;
  countryName: string;
}

export enum PlanningTripFields {
  CabinClass = 'cabinClass',
  LeavingFrom = 'leavingFrom',
  Travelers = 'travelers',
  TravellingTo = 'travellingTo',
  Range = 'range',
}

export interface PlanningTripForm {
  [PlanningTripFields.LeavingFrom]: string;
  [PlanningTripFields.TravellingTo]: string;
  [PlanningTripFields.Travelers]?: number;
  [PlanningTripFields.Range]: {
    [Range.From]: string;
    [Range.To]: string;
  };
  [PlanningTripFields.CabinClass]?: string;
}

@Component({
  selector: 'planning-trip-modal',
  templateUrl: './planning-trip-modal.component.html',
  styleUrls: ['./planning-trip-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlanningTripModalComponent extends BaseFormComponent<
  null,
  { 'planning-trip': PlanningTripForm }
> {
  airports$: Observable<AutocompleteItem[]> = this.apiService.airports().pipe(
    map((airports) => {
      const locationCount = airports.reduce((acc: Record<string, number>, airport) => {
        const locationKey = [airport.cityName, airport.countryName].join(',');
        if (!acc[locationKey]) {
          acc[locationKey] = 0;
        }
        acc[locationKey]++;
        return acc;
      }, {});

      const groupedByCountry = airports.reduce((acc: Record<string, AutocompleteItem>, airport) => {
        const key = [airport.cityName, airport.countryName].join(',');
        if (!acc[key]) {
          acc[key] = { options: [] };

          if (locationCount[key] > 1) {
            acc[key].groupData = {
              title: [airport.cityName, airport.countryName].join(','),
              icons: ['apartment'],
              value: '',
            };
          }
        }

        const value = [airport.countryName, `(${airport.locationCode})`].join(' ');
        this.airportsValuesMap[value] = airport.id;

        const option = {
          title:
            locationCount[key] > 1
              ? [airport.cityName, airport.countryName].join(',')
              : airport.locationName,
          suffix: airport.locationCode,
          icons: ['travel'],
          value,
        };

        acc[key].options.push(option);

        this.airports.push(option);
        return acc;
      }, {});

      Object.keys(groupedByCountry).map((country) => {
        return {
          options: groupedByCountry[country],
        };
      });

      const result = Object.values(groupedByCountry);
      result.forEach((item) => this.sortOptions(item.options));
      return result;
    }),
    shareReplay(1),
  );

  protected readonly title = 'Planning your next trip';

  private airports: AutocompleteOption[] = [];

  private formKey = 'planning-trip' as const;
  private readonly travelerClassOptions: Option[] = [
    { label: '1-passenger', value: '1' },
    { label: '2-passenger', value: '2' },
    { label: '3-passenger', value: '3' },
    { label: '4-passenger', value: '4' },
    { label: 'More than 4', value: '5' },
  ];

  private readonly cabinClassOptions: Option[] = [
    { label: 'First', value: PortalServiceClassType.FIRST },
    { label: 'Business', value: PortalServiceClassType.BUSINESS },
    { label: 'Premium Economy', value: PortalServiceClassType.PREMIUM_ECONOMY },
    { label: 'Economy', value: PortalServiceClassType.ECONOMY },
  ];

  private readonly toAndFromValidator = {
    expression: () => {
      const form: { 'planning-trip': PlanningTripForm } = this.form.getRawValue() as {
        'planning-trip': PlanningTripForm;
      };
      const data: PlanningTripForm = form[this.formKey];
      const leavingFromValue: string = data[PlanningTripFields.LeavingFrom] ?? '';
      const travellingToValue: string = data[PlanningTripFields.TravellingTo] ?? '';

      if (!leavingFromValue || !travellingToValue) {
        return true;
      }

      return leavingFromValue?.trim().toLowerCase() !== travellingToValue?.trim().toLowerCase();
    },
    message: 'Departure and destination cannot be the same',
  };
  private readonly isAirportAllowedValidator = {
    expression: (control: AbstractControl<string>) => {
      if (!control.value) {
        return false;
      }
      const value = control.value.trim().toLowerCase();
      return this.airports.some((airport) => airport.value.trim().toLowerCase() === value);
    },
    message: 'Please select a valid airport from the list',
  };

  private readonly isValidFromValidator = {
    expression: (control: AbstractControl<RangeData>) => {
      if (!control.value) {
        return true;
      }

      const from = control.value.from;
      const to = control.value.to;

      if (!to && !from) {
        return true;
      }

      return !(from && !isValidDateString(from));
    },
    message: 'Please add a valid date',
  };

  private readonly isValidToValidator = {
    expression: (control: AbstractControl<RangeData>) => {
      if (!control.value) {
        return true;
      }

      const from = control.value.from;
      const to = control.value.to;

      if (!to && !from) {
        return true;
      }

      return !(to && !isValidDateString(to));
    },
    message: 'Please add a valid date',
  };

  private readonly isFromRequired = {
    expression: (control: AbstractControl<RangeData>) => {
      const from = control.value?.from;

      return !!from;
    },
    message: 'Field is required',
  };

  airportsValuesMap: Record<string, string> = {};

  constructor(
    private apiService: ApiService,
    private apiProtectedService: ApiProtectedService,
    private router: Router,
    private errorService: ErrorService,
    protected popupsService: PopupsService,
  ) {
    super();
  }

  override getFields(): FormlyFieldConfig[] {
    return [
      {
        key: this.formKey,
        className: 'w-100',
        type: this.formKey,
        fieldGroup: [
          {
            fieldGroupClassName: 'd-flex g-4 planning-trip-row pb-4',
            fieldGroup: [
              {
                key: PlanningTripFields.LeavingFrom,
                className: 'w-100',
                type: 'input-with-autocomplete',
                validators: {
                  toAndFrom: this.toAndFromValidator,
                  isAirportAllowed: this.isAirportAllowedValidator,
                },
                props: {
                  label: 'Leaving from',
                  placeholder: 'Leaving from',
                  prefixIcon: 'flight_takeoff',
                  autocompleteOptions$: this.airports$,
                  required: true,
                },

                hooks: {
                  onInit: (field) => {
                    const control = field.formControl;
                    control?.valueChanges.subscribe(() => {
                      const toControl = this.form
                        ?.get('planning-trip')
                        ?.get([PlanningTripFields.TravellingTo]);

                      if (toControl?.errors && toControl?.errors.toAndFrom) {
                        toControl.updateValueAndValidity();
                      }
                    });
                  },
                },
              },
              {
                key: PlanningTripFields.TravellingTo,
                className: 'w-100',
                type: 'input-with-autocomplete',
                validators: {
                  toAndFrom: this.toAndFromValidator,
                  isAirportAllowed: this.isAirportAllowedValidator,
                },
                props: {
                  label: 'Travelling to',
                  placeholder: 'Travelling to',
                  prefixIcon: 'flight_land',
                  required: true,
                  autocompleteOptions$: this.airports$,
                },
                hooks: {
                  onInit: (field) => {
                    const control = field.formControl;
                    control?.valueChanges.subscribe(() => {
                      const fromControl = this.form
                        ?.get('planning-trip')
                        ?.get([PlanningTripFields.LeavingFrom]);

                      if (fromControl?.errors && fromControl?.errors.toAndFrom) {
                        fromControl.updateValueAndValidity();
                      }
                    });
                  },
                },
              },
            ],
          },
          {
            fieldGroupClassName: 'd-flex g-4 planning-trip-row',
            fieldGroup: [
              {
                key: PlanningTripFields.Range,
                className: 'planning-trip-range',
                type: 'range-picker',
                props: {
                  labelFrom: 'Depart date',
                  labelTo: 'Return date',
                  placeholderTo: 'Return date',
                  placeholderFrom: 'Depart date',
                  required: true,
                },
                validators: {
                  isValidFromValidator: this.isValidFromValidator,
                  isValidToValidator: this.isValidToValidator,
                  isFromRequired: this.isFromRequired,
                },
              },
              {
                key: PlanningTripFields.Travelers,
                type: 'dropdown',
                className: 'w-100 planning-trip-item',
                props: {
                  label: 'Travelers',
                  placeholder: 'Travelers',
                  required: true,
                  options: of(this.travelerClassOptions),
                },
              },
              {
                key: PlanningTripFields.CabinClass,
                type: 'dropdown',
                className: 'w-100 planning-trip-item',
                props: {
                  label: 'Cabin class',
                  placeholder: 'Cabin class',
                  options: of(this.cabinClassOptions),
                },
              },
            ],
          },
        ],
      },
    ];
  }

  override submit(): void {
    const data = this.model['planning-trip'];
    const from = data[PlanningTripFields.Range]?.from;
    const to = data[PlanningTripFields.Range]?.to;
    const travelers = data[PlanningTripFields.Travelers];
    const cabinClass = data[PlanningTripFields.CabinClass] as PortalServiceClassType | undefined;

    if (!travelers || !from) {
      return;
    }

    const payload: NewLead = {
      departureAirportId: this.airportsValuesMap[data[PlanningTripFields.LeavingFrom]],
      arrivalAirportId: this.airportsValuesMap[data[PlanningTripFields.TravellingTo]],
      passengerCount: +travelers,
      departureAt: '',
    };

    if (from) {
      const fromDay = formatStringToDate(from);

      if (fromDay) {
        payload.departureAt = toISO8601(fromDay);
      }
    }

    if (to) {
      const toDay = formatStringToDate(to);

      if (toDay) {
        payload.returnAt = toISO8601(toDay);
      }
    }

    if (cabinClass) {
      payload.serviceClass = cabinClass;
    }

    super.submit();

    this.apiProtectedService
      .portalNewLead(payload)
      .pipe(take(1), handleError(this.errorService))
      .subscribe(({ data }) => {
        if (!data) {
          return;
        }

        const path = [`${data.portalNewLead.portalLink}`];
        void this.router.navigate(path);
        this.popupsService.hideLast();
      });
  }

  private sortOptions(options: AutocompleteOption[]): void {
    options.sort((a, b) => {
      const titleComparison = a.title.localeCompare(b.title);
      if (titleComparison !== 0) return titleComparison;
      return (a.suffix ?? '').localeCompare(b.suffix ?? '');
    });
  }

  hideModal() {
    this.popupsService.hideLast();
  }
}
